import { AnalyticsBrowser, Context } from '@segment/analytics-next';
import { env } from '../../constants';

const WRITE_KEY = env.VITE_SEGMENT_WRITE_KEY;
const SEGMENT_CDN_URL = env.VITE_SEGMENT_CDN_URL;
const SEGMENT_HOST_API_URL = env.VITE_SEGMENT_API_HOST_URL;

let analytics: AnalyticsBrowser | null = null;
let isAnalyticsLoaded = false;
let callbackQueue: (() => void)[] = [];
let isConsentGiven: boolean | null = null;

export const getConsentGiven = () => {
  if (isConsentGiven === null) {
    console.warn('Tracking consent status is not loaded yet.');
  }
  return isConsentGiven;
};

export const setConsentGiven = (consent: boolean) => {
  isConsentGiven = consent;

  if (consent === false) {
    callbackQueue = [];
  }
};

export const loadAnalytics = async () => {
  if (isAnalyticsLoaded) return;
  analytics = AnalyticsBrowser.load(
    { writeKey: WRITE_KEY, cdnURL: SEGMENT_CDN_URL },
    {
      integrations: {
        'Segment.io': {
          apiHost: SEGMENT_HOST_API_URL
        }
      }
    }
  );

  await analytics.ready(() => {
    isAnalyticsLoaded = true;

    callbackQueue.forEach(callback => callback());
    callbackQueue = [];
  });
};

export const withAnalytics = async (
  callback: (analytics: AnalyticsBrowser) => Promise<Context | void>
): Promise<Context | void> => {
  if (isConsentGiven === false) {
    return;
  }

  if (!analytics) {
    console.warn(
      'Analytics is not loaded. Ensure loadAnalytics is called after consent is obtained.'
    );
  }

  if (isAnalyticsLoaded && analytics) {
    return await callback(analytics);
  } else {
    callbackQueue.push(() => {
      if (analytics) {
        callback(analytics).catch(error => {
          console.error('Error in queued tracking events callback:', error);
        });
      }
    });
  }
};

export const identify = (
  userId: string,
  traits?: object
): Promise<Context | void> =>
  withAnalytics(analytics => analytics.identify(userId, traits));

export const track = (
  event: string,
  properties?: object
): Promise<Context | void> =>
  withAnalytics(analytics => analytics.track(event, properties));

export const page = (
  category?: string,
  name?: string,
  properties?: object
): Promise<Context | void> =>
  withAnalytics(analytics => analytics.page(category, name, properties));

export const screen = (
  name: string,
  properties?: object
): Promise<Context | void> =>
  withAnalytics(analytics => analytics.screen(name, properties));
